import React from 'react';
import { createRoot } from 'react-dom/client';
import { DesignSystemProvider } from '@revdotcom/design-system/components/DesignSystemProvider';
// Import from LOCAL src/ directory (copied component)
import { AudioTrimmerCutter } from './src/AudioTrimmerCutter';
// Import design system CSS (scoped to prevent conflicts with Webflow styles)
import '@revdotcom/design-system/styles/scoped.css';
// Import local CSS
import './styles/audio-trimmer.css';
/**
 * Pre-configured themes for common use cases
 */
export const RevWebflowThemes = {
    /** Dark theme matching Webflow's purple aesthetic & Figma design */
    dark: {
        cardBorderRadius: '16px',
        darkMode: true,
        readyBg: '#120F20',
        readyText: 'rgba(255, 255, 255, 0.70)',
        readyButtonBg: '#7C5EF3',
        readyButtonText: 'white',
        readyButtonHoverBg: '#6B4DD9'
    },
    /** Light theme (original Rev.com styling) */
    light: {
        cardBorderRadius: '8px',
        darkMode: false,
        readyBg: 'white',
        readyText: '#1a1a1a',
        readyButtonBg: '#667eea',
        readyButtonText: 'white',
        readyButtonHoverBg: '#5568d3'
    }
};
/**
 * Applies theme configuration by setting CSS variables on the container element
 */
function applyTheme(container, theme) {
    if (!theme) {
        return;
    }
    // Apply CSS variables
    if (theme.cardBorderRadius) {
        container.style.setProperty('--card-border-radius', theme.cardBorderRadius);
    }
    // Apply ready state theme variables
    if (theme.readyBg) {
        container.style.setProperty('--ready-bg', theme.readyBg);
    }
    if (theme.readyText) {
        container.style.setProperty('--ready-text', theme.readyText);
    }
    if (theme.readyButtonBg) {
        container.style.setProperty('--ready-button-bg', theme.readyButtonBg);
    }
    if (theme.readyButtonText) {
        container.style.setProperty('--ready-button-text', theme.readyButtonText);
    }
    if (theme.readyButtonHoverBg) {
        container.style.setProperty('--ready-button-hover-bg', theme.readyButtonHoverBg);
    }
    // Apply dark mode class
    if (theme.darkMode) {
        container.classList.add('dark-mode');
    }
    else {
        container.classList.remove('dark-mode');
    }
}
/**
 * Fetches AWS upload configuration from the Rev.com API.
 * Always same-origin: the config endpoint is co-located on every environment, so a
 * cross-origin fetch (e.g. a lower env whose page points at prod) is CORS-blocked and
 * IP-throttled, which fails the whole tool init.
 * Reuses the voice recorder config endpoint since it provides the same AWS credentials.
 */
async function fetchAwsConfig() {
    const configUrl = `${window.location.origin}/api/voice-recorder/config`;
    try {
        const response = await fetch(configUrl, {
            credentials: 'include', // Include cookies for authentication if needed
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch AWS config: ${response.status} ${response.statusText}`);
        }
        const config = await response.json();
        return config;
    }
    catch (error) {
        console.error('[RevWebflow] Error fetching AWS config:', error);
        throw new Error('Failed to load audio trimmer configuration. Please try again later.');
    }
}
/**
 * Initializes the Audio Trimmer component in a Webflow page
 *
 * @example
 * ```javascript
 * // Simplest usage - AWS config fetched automatically
 * RevWebflow.initAudioTrimmer({
 *     containerId: 'audio-trimmer-container',
 *     isAuthenticated: false
 * });
 * ```
 *
 * @example
 * ```javascript
 * // Usage with dark theme
 * RevWebflow.initAudioTrimmer({
 *     containerId: 'audio-trimmer-container',
 *     isAuthenticated: false,
 *     theme: RevWebflowAudioTrimmerThemes.dark
 * });
 * ```
 *
 * @param options - Configuration options including container ID and theme
 */
export async function initAudioTrimmer(options) {
    const container = document.getElementById(options.containerId);
    if (!container) {
        console.error('[RevWebflow] Container element not found! ID:', options.containerId);
        return;
    }
    // Provide analytics stub if Segment is not available
    if (!window.analytics) {
        window.analytics = {
            track: (eventName, properties) => {
                // No-op: Analytics not available
            }
        };
    }
    try {
        // Apply theme before rendering
        applyTheme(container, options.theme);
        // Fetch AWS config if not provided
        let awsUploadInfo = options.awsUploadInfo;
        if (!awsUploadInfo) {
            awsUploadInfo = await fetchAwsConfig();
        }
        // Create React root and render component
        const root = createRoot(container);
        root.render(React.createElement(DesignSystemProvider, { scoped: true },
            React.createElement(AudioTrimmerCutter, { awsUploadInfo: awsUploadInfo, isAuthenticated: options.isAuthenticated })));
    }
    catch (error) {
        console.error('[RevWebflow] Error during initialization:', error);
        console.error('[RevWebflow] Error stack:', error instanceof Error ? error.stack : 'No stack');
        // Display error message in the container
        container.innerHTML = `
            <div style="padding: 20px; text-align: center; color: #d32f2f; background: #ffebee; border-radius: 8px;">
                <h3>Failed to initialize audio trimmer</h3>
                <p>${error instanceof Error ? error.message : 'An unknown error occurred'}</p>
                <p style="font-size: 14px; margin-top: 10px;">Please check the console for more details.</p>
            </div>
        `;
    }
}
if (typeof window !== 'undefined') {
    // Extend existing RevWebflow object if it exists (e.g., from voice recorder)
    window.RevWebflow = Object.assign(Object.assign({}, (window.RevWebflow || {})), { initAudioTrimmer });
    // Per-bundle namespace so themes don't collide with sibling Webflow bundles
    // (voice recorder / caption converter) that have different theme shapes.
    window.RevWebflowAudioTrimmerThemes = RevWebflowThemes;
}
